import React from "react";
import {Controller, useForm} from "react-hook-form";
import {Form} from "react-bootstrap";
import SettingsPreferencesCard from "components/newSettings/Settings.preferences.card";
import {useSelector, useDispatch} from "react-redux";
import {currentPreferenceSelector} from "components/settings/preferences.selector";
import {
    NextTask,
    NextTaskKeysList,
    PDFViewer,
    PDFViewerKeysList,
    WhenApprovingTask,
    WhenApprovingTaskKeysList,
} from "types/nextTask";
import * as Api from 'utils/api/api';
import {getUsersCurrentContextId} from "components/store/application.reducers";
import ErrorInContext from "components/common/errorInContext";
import debounce from "lodash/debounce";
import {loadUserData} from "components/usercontext/user.action";


const mapPreferencesToFormValues = (preference: any) => ({
    ...preference,
    showCommentOnApprove: preference.showCommentOnApprove ? WhenApprovingTask.OPEN_COMMENT : WhenApprovingTask.APPROVE_DIRECTLY,
    pdfViewer: window.localStorage.getItem("@@pdf/defaultPDF") === "false" ? PDFViewer.ALTERNATIVE_PDF : PDFViewer.BROWSER_PDF,
    nextTask: preference.nextTask ? NextTask.OPEN_NEXT : NextTask.RETURN_TASK,
});

const PreferencesContent = ({translate}: { translate: (key: string) => string }): React.ReactElement => {
    const initialPreference = useSelector(currentPreferenceSelector);
    const context = useSelector(getUsersCurrentContextId);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<{ title: string, text: string, more?: string | null } | null>(null);

    const {control, handleSubmit, reset} = useForm({
        defaultValues: mapPreferencesToFormValues(initialPreference),
    });

    const dispatch = useDispatch();

    React.useEffect(() => {
        dispatch(loadUserData());
        return () => {
            onSubmit.cancel();
        };
    }, []);

    React.useEffect(() => {
        reset(mapPreferencesToFormValues(initialPreference));
    }, [initialPreference, reset]);

    const onSubmit = debounce(async (data: any) => {
        if (loading) {
            return;
        }

        setLoading(true);
        setError(null);

        const dataToSend = {
            nextTask: data.nextTask === NextTask.OPEN_NEXT,
            showCommentOnApprove: data.showCommentOnApprove === WhenApprovingTask.OPEN_COMMENT
        }

        try {
            await Api.updateUserSettings(dataToSend, context);
            dispatch(loadUserData());

        } catch (error) {
            setError({
                title: translate("settings.preferences.saveErrorTitle"),
                text: translate("settings.preferences.saveErrorText"),
                more: error.errorMessages || null
            });

            dispatch(loadUserData());
        } finally {
            setLoading(false);
        }
    }, 800);

    const saveToLocalStorage = (data: any) => {
        window.localStorage.setItem("@@pdf/defaultPDF", data.pdfViewer === PDFViewer.BROWSER_PDF);
    }

    const handleChange = (onChange: (value: any) => void, e: boolean | string | number, isPDFViewer: boolean = false) => {
        onChange(e);
        handleSubmit(isPDFViewer ? saveToLocalStorage : onSubmit)();
    }


    const renderControllerContent = (
        {field: {onChange, onBlur, value}}: any,
        name: string,
        options: any[],
        valueMap: any
    ) => {
        return (
            <>
                {options.map((key) => (
                    <div key={`${valueMap[key]}-${key}`}>
                        <Form.Check
                            name={`${name}-option`}
                            value={valueMap[key]}
                            id={valueMap[key]}
                            type="radio"
                            className="radio"
                            checked={value === valueMap[key]}
                            onBlur={onBlur}
                            onChange={(e) => handleChange(onChange, e.target.value, valueMap === PDFViewer)}
                            aria-label={
                                translate("settings.other.afterHandle ") +
                                translate(`settings.other.${valueMap[key]}`)
                            }
                            label={
                                <div>
                                        <span className={"font-16"}>
                                        {translate(`settings.other.${valueMap[key]}`)}
                                        </span>
                                    {name === "pdfViewer" && valueMap === PDFViewer && (
                                        <div className={"mt-2"}>
                                            {translate(`settings.other.more${valueMap[key]}`)}
                                        </div>
                                    )}
                                </div>
                            }
                            data-testid={`__tst_${valueMap[key]}`}
                        />
                        <br/>
                    </div>
                ))}
            </>)
    }

    const renderRadioGroup = (name: string, options: any[], valueMap: any) => (
        <Controller
            name={name as keyof typeof initialPreference}
            control={control}
            render={(fieldProps) => renderControllerContent(fieldProps, name, options, valueMap)}
        />
    );


    return (
        <form>
            {error && <ErrorInContext {...error}
                                      onCloseCallback={() => {
                                          setError(null)
                                      }}
            />}


            <SettingsPreferencesCard
                title={translate("settings.preferences.handlingTasksTitle")}
                subtitle={translate("settings.preferences.whenApprovingATask")}
                contentInput={
                    <>
                        {renderRadioGroup("showCommentOnApprove", WhenApprovingTaskKeysList, WhenApprovingTask)}
                        <h4 className={"mt-10 mb-8"}>{translate("settings.preferences.whenTaskIsHandled")}</h4>
                        {renderRadioGroup("nextTask", NextTaskKeysList, NextTask)}
                    </>
                }
            />

            <SettingsPreferencesCard
                title={translate("settings.preferences.pdfViewerTitle")}
                subtitle={translate("settings.preferences.pdfViewerSubtitle")}
                contentInput={renderRadioGroup("pdfViewer", PDFViewerKeysList, PDFViewer)}
            />

        </form>
    );

}
export default PreferencesContent;
